import { useState } from 'react'
import { Link } from 'react-router-dom'
//import { useAuth } from '../../contexts/AuthContext'

// Mock data - in production this comes from API
const MOCK_PROFILE = {
  name: 'Sarah Kim',
  email: 'sarah.kim@acme.com',
  avatarInitial: 'S',
  roles: ['Engineering Manager', 'Hiring Committee Member'],
  department: 'Engineering',
  timezone: 'America/Los_Angeles',
  joinedAt: 'March 2024',
  
  // Evaluator identity
  evaluator: {
    isActive: true,
    expertise: ['Backend Systems', 'Distributed Systems', 'Technical Leadership'],
    level: 'Staff',
    isBarRaiser: true,
    barRaiserSince: 'September 2024',
    totalEvaluations: 47,
    activeRoles: 3,
  },
  
  // Evaluation tendencies (AI-generated, shown optionally)
  tendencies: {
    available: true,
    insights: [
      {
        type: 'observation',
        text: 'You tend to weight technical depth more heavily than peers in system design interviews.',
      },
      {
        type: 'observation', 
        text: 'Your evaluations are 23% more detailed than the team average.',
      },
      {
        type: 'calibration',
        text: 'Your hire/no-hire decisions align with final outcomes 89% of the time.',
      },
    ],
    lastUpdated: 'December 2025',
  },
}

const TIMEZONES = [
  { value: 'America/Los_Angeles', label: 'Pacific Time (Los Angeles)' },
  { value: 'America/Denver', label: 'Mountain Time (Denver)' },
  { value: 'America/Chicago', label: 'Central Time (Chicago)' },
  { value: 'America/New_York', label: 'Eastern Time (New York)' },
  { value: 'Europe/London', label: 'GMT (London)' },
  { value: 'Europe/Paris', label: 'CET (Paris)' },
  { value: 'Europe/Berlin', label: 'CET (Berlin)' },
  { value: 'Asia/Singapore', label: 'SGT (Singapore)' },
  { value: 'Asia/Tokyo', label: 'JST (Tokyo)' },
  { value: 'Australia/Sydney', label: 'AEDT (Sydney)' },
]

const EXPERTISE_OPTIONS = [
  'Backend Systems',
  'Frontend Development',
  'Mobile Development',
  'Distributed Systems',
  'Machine Learning',
  'Data Engineering',
  'Infrastructure',
  'Security',
  'Technical Leadership',
  'System Design',
  'Product Thinking',
  'People Management',
]

const LEVEL_OPTIONS = [
  { value: 'junior', label: 'Junior' },
  { value: 'mid', label: 'Mid-Level' },
  { value: 'senior', label: 'Senior' },
  { value: 'staff', label: 'Staff' },
  { value: 'principal', label: 'Principal' },
  { value: 'director', label: 'Director+' },
]

function ArrowLeftIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
    </svg>
  )
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
    </svg>
  )
}

function ShieldCheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
    </svg>
  )
}

function SparklesIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
    </svg>
  )
}

function XMarkIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  )
}

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
    </svg>
  )
}

export default function ProfilePage() {
  //const { user } = useAuth()
  const [profile, setProfile] = useState(MOCK_PROFILE)
  const [hasChanges, setHasChanges] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [showExpertiseDropdown, setShowExpertiseDropdown] = useState(false)

  const handleChange = (field: string, value: any) => {
    setProfile(prev => ({ ...prev, [field]: value }))
    setHasChanges(true)
    setSaved(false)
  }

  const handleEvaluatorChange = (field: string, value: any) => {
    setProfile(prev => ({
      ...prev,
      evaluator: { ...prev.evaluator, [field]: value }
    }))
    setHasChanges(true)
    setSaved(false)
  }

  const handleAddExpertise = (expertise: string) => {
    if (!profile.evaluator.expertise.includes(expertise)) {
      handleEvaluatorChange('expertise', [...profile.evaluator.expertise, expertise])
    }
    setShowExpertiseDropdown(false)
  }

  const handleRemoveExpertise = (expertise: string) => {
    handleEvaluatorChange(
      'expertise',
      profile.evaluator.expertise.filter(e => e !== expertise)
    )
  }

  const handleSave = async () => {
    setSaving(true)
    await new Promise(resolve => setTimeout(resolve, 800))
    setSaving(false)
    setHasChanges(false)
    setSaved(true)
    setTimeout(() => setSaved(false), 3000)
  }

  const availableExpertise = EXPERTISE_OPTIONS.filter(
    e => !profile.evaluator.expertise.includes(e)
  )

  return (
    <div className="min-h-screen bg-surface-50">
      {/* Header */}
      <header className="bg-white border-b border-surface-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link 
              to="/dashboard" 
              className="flex items-center gap-2 text-surface-600 hover:text-surface-900 transition-colors"
            >
              <ArrowLeftIcon className="w-5 h-5" />
              <span className="font-medium">Back to Dashboard</span>
            </Link>

            <Link to="/" className="flex items-center gap-2">
              <img src="/images/logo.png" alt="JumpstoneHR" className="w-8 h-8" />
              <span className="font-semibold text-xl text-brand-600">JumpstoneHR</span>
            </Link>

            <div className="w-[140px]" /> {/* Spacer for centering */}
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page header */}
        <div className="mb-8">
          <h1 className="text-2xl font-display font-semibold text-surface-900">Your Profile</h1>
          <p className="mt-1 text-surface-600">
            Your identity as an evaluator in the hiring process.
          </p>
        </div>

        <div className="space-y-6">
          {/* Identity Card */}
          <div className="bg-white rounded-2xl border border-surface-200 shadow-sm overflow-hidden">
            <div className="p-6 sm:p-8">
              <div className="flex items-start gap-6">
                {/* Avatar */}
                <div className="shrink-0">
                  <div className="w-20 h-20 rounded-2xl bg-brand-100 text-brand-700 flex items-center justify-center text-3xl font-semibold">
                    {profile.avatarInitial}
                  </div>
                </div>
                
                {/* Basic info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h2 className="text-xl font-semibold text-surface-900">{profile.name}</h2>
                      <p className="text-surface-500">{profile.email}</p>
                    </div>
                    {profile.evaluator.isBarRaiser && (
                      <div className="shrink-0 flex items-center gap-2 px-3 py-1.5 rounded-lg bg-amber-50 border border-amber-200">
                        <ShieldCheckIcon className="w-5 h-5 text-amber-600" />
                        <span className="text-sm font-medium text-amber-700">Bar Raiser</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="mt-4 flex flex-wrap gap-2">
                    {profile.roles.map((role) => (
                      <span 
                        key={role}
                        className="inline-flex px-3 py-1 rounded-lg bg-surface-100 text-sm font-medium text-surface-700"
                      >
                        {role}
                      </span>
                    ))}
                  </div>
                  
                  <p className="mt-3 text-sm text-surface-500">
                    {profile.department} · Member since {profile.joinedAt}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Personal Details */}
          <div className="bg-white rounded-2xl border border-surface-200 shadow-sm overflow-hidden">
            <div className="p-6 sm:p-8 border-b border-surface-100">
              <h3 className="text-lg font-semibold text-surface-900">Personal Details</h3>
              <p className="mt-1 text-surface-500">Basic information about you.</p>
            </div>
            
            <div className="p-6 sm:p-8 space-y-6">
              {/* Name */}
              <div className="grid sm:grid-cols-3 gap-4 items-start">
                <div>
                  <label className="block text-sm font-medium text-surface-900">Full name</label>
                  <p className="text-sm text-surface-500">How you appear in evaluations</p>
                </div>
                <div className="sm:col-span-2">
                  <input
                    type="text"
                    value={profile.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    className="block w-full rounded-lg border border-surface-300 px-4 py-2.5 text-surface-900 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 transition-colors"
                  />
                </div>
              </div>

              {/* Email */}
              <div className="grid sm:grid-cols-3 gap-4 items-start">
                <div>
                  <label className="block text-sm font-medium text-surface-900">Email</label>
                  <p className="text-sm text-surface-500">Your work email address</p>
                </div>
                <div className="sm:col-span-2">
                  <div className="flex items-center gap-3">
                    <input
                      type="email"
                      value={profile.email}
                      disabled
                      className="block flex-1 rounded-lg border border-surface-200 bg-surface-50 px-4 py-2.5 text-surface-600 cursor-not-allowed"
                    />
                    <span className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-50 text-emerald-700 text-sm font-medium">
                      <CheckIcon className="w-4 h-4" />
                      Verified
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-surface-400">
                    Email is managed by your organization and cannot be changed here.
                  </p>
                </div>
              </div>

              {/* Timezone */}
              <div className="grid sm:grid-cols-3 gap-4 items-start">
                <div>
                  <label className="block text-sm font-medium text-surface-900">Timezone</label>
                  <p className="text-sm text-surface-500">For scheduling and timestamps</p>
                </div>
                <div className="sm:col-span-2 relative">
                  <select
                    value={profile.timezone}
                    onChange={(e) => handleChange('timezone', e.target.value)}
                    className="block w-full appearance-none rounded-lg border border-surface-300 px-4 py-2.5 text-surface-900 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 transition-colors cursor-pointer"
                  >
                    {TIMEZONES.map((tz) => (
                      <option key={tz.value} value={tz.value}>{tz.label}</option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4">
                    <svg className="w-4 h-4 text-surface-400" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 15L12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Evaluator Identity */}
          <div className="bg-white rounded-2xl border border-surface-200 shadow-sm overflow-hidden">
            <div className="p-6 sm:p-8 border-b border-surface-100">
              <h3 className="text-lg font-semibold text-surface-900">Evaluator Identity</h3>
              <p className="mt-1 text-surface-500">
                How you are identified when providing candidate evaluations. This information helps calibrate feedback and match you to relevant interviews.
              </p>
            </div>
            
            <div className="p-6 sm:p-8 space-y-6">
              {/* Areas of Expertise */}
              <div className="grid sm:grid-cols-3 gap-4 items-start">
                <div>
                  <label className="block text-sm font-medium text-surface-900">Areas of expertise</label>
                  <p className="text-sm text-surface-500">Technical domains you can evaluate</p>
                </div>
                <div className="sm:col-span-2">
                  <div className="flex flex-wrap gap-2 mb-3">
                    {profile.evaluator.expertise.map((expertise) => (
                      <span 
                        key={expertise}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-brand-50 text-brand-700 text-sm font-medium border border-brand-200"
                      >
                        {expertise}
                        <button
                          onClick={() => handleRemoveExpertise(expertise)}
                          className="p-0.5 hover:bg-brand-100 rounded transition-colors"
                        >
                          <XMarkIcon className="w-3.5 h-3.5" />
                        </button>
                      </span>
                    ))}
                  </div>
                  
                  <div className="relative">
                    <button
                      onClick={() => setShowExpertiseDropdown(!showExpertiseDropdown)}
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-surface-300 text-sm text-surface-600 hover:border-brand-300 hover:text-brand-600 transition-colors"
                    >
                      <PlusIcon className="w-4 h-4" />
                      Add expertise
                    </button>
                    
                    {showExpertiseDropdown && availableExpertise.length > 0 && (
                      <div className="absolute left-0 top-full mt-2 w-64 bg-white rounded-xl shadow-lg border border-surface-200 py-2 z-10">
                        {availableExpertise.map((expertise) => (
                          <button
                            key={expertise}
                            onClick={() => handleAddExpertise(expertise)}
                            className="w-full text-left px-4 py-2 text-sm text-surface-700 hover:bg-surface-50 transition-colors"
                          >
                            {expertise}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Seniority Level */}
              <div className="grid sm:grid-cols-3 gap-4 items-start">
                <div>
                  <label className="block text-sm font-medium text-surface-900">Seniority level</label>
                  <p className="text-sm text-surface-500">Your experience level</p>
                </div>
                <div className="sm:col-span-2 relative">
                  <select
                    value={profile.evaluator.level.toLowerCase()}
                    onChange={(e) => handleEvaluatorChange('level', e.target.value)}
                    className="block w-full appearance-none rounded-lg border border-surface-300 px-4 py-2.5 text-surface-900 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 transition-colors cursor-pointer"
                  >
                    {LEVEL_OPTIONS.map((level) => (
                      <option key={level.value} value={level.value}>{level.label}</option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4">
                    <svg className="w-4 h-4 text-surface-400" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 15L12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Bar Raiser Status */}
              <div className="grid sm:grid-cols-3 gap-4 items-start">
                <div>
                  <label className="block text-sm font-medium text-surface-900">Bar raiser</label>
                  <p className="text-sm text-surface-500">Designated to uphold hiring standards</p>
                </div>
                <div className="sm:col-span-2">
                  {profile.evaluator.isBarRaiser ? (
                    <div className="p-4 rounded-xl bg-amber-50 border border-amber-200">
                      <div className="flex items-start gap-3">
                        <ShieldCheckIcon className="w-6 h-6 text-amber-600 shrink-0" />
                        <div>
                          <p className="font-medium text-amber-900">You are a Bar Raiser</p>
                          <p className="text-sm text-amber-700 mt-1">
                            Since {profile.evaluator.barRaiserSince}. Your evaluations carry additional weight in hire/no-hire decisions to ensure consistent standards.
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="p-4 rounded-xl bg-surface-50 border border-surface-200">
                      <p className="text-sm text-surface-600">
                        Bar raisers are designated by your organization's hiring committee. Contact your hiring lead to learn more.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Evaluation Activity */}
              <div className="grid sm:grid-cols-3 gap-4 items-start">
                <div>
                  <label className="block text-sm font-medium text-surface-900">Evaluation activity</label>
                  <p className="text-sm text-surface-500">Your contribution summary</p>
                </div>
                <div className="sm:col-span-2">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 rounded-xl bg-surface-50">
                      <p className="text-2xl font-semibold text-surface-900">{profile.evaluator.totalEvaluations}</p>
                      <p className="text-sm text-surface-500">Total evaluations</p>
                    </div>
                    <div className="p-4 rounded-xl bg-surface-50">
                      <p className="text-2xl font-semibold text-surface-900">{profile.evaluator.activeRoles}</p>
                      <p className="text-sm text-surface-500">Active roles</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* AI-Generated Tendencies (Optional) */}
          {profile.tendencies.available && (
            <div className="bg-white rounded-2xl border border-surface-200 shadow-sm overflow-hidden">
              <div className="p-6 sm:p-8 border-b border-surface-100">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center shrink-0">
                    <SparklesIcon className="w-5 h-5 text-violet-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-surface-900">Evaluation Tendencies</h3>
                    <p className="mt-1 text-surface-500">
                      AI-generated insights based on your evaluation history. Updated {profile.tendencies.lastUpdated}.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="p-6 sm:p-8">
                <div className="space-y-4">
                  {profile.tendencies.insights.map((insight, idx) => (
                    <div 
                      key={idx}
                      className={`flex items-start gap-3 p-4 rounded-xl ${
                        insight.type === 'calibration' 
                          ? 'bg-emerald-50 border border-emerald-100' 
                          : 'bg-surface-50 border border-surface-100'
                      }`}
                    >
                      <div className={`w-2 h-2 rounded-full mt-2 shrink-0 ${
                        insight.type === 'calibration' ? 'bg-emerald-500' : 'bg-surface-400'
                      }`} />
                      <p className={`text-sm ${
                        insight.type === 'calibration' ? 'text-emerald-800' : 'text-surface-700'
                      }`}>
                        {insight.text}
                      </p>
                    </div>
                  ))}
                </div>
                
                <p className="mt-6 text-xs text-surface-400">
                  These insights are for your reference only and are not visible to other evaluators or candidates.
                </p>
              </div>
            </div>
          )}

          {/* Attribution footer */}
          <div className="p-4 rounded-xl bg-surface-100 border border-surface-200">
            <div className="flex items-center gap-3 text-sm text-surface-600">
              <ShieldCheckIcon className="w-5 h-5 text-surface-400" />
              <span>
                Your identity is attached to every evaluation you submit. This ensures accountability and enables fair, auditable hiring decisions.
              </span>
            </div>
          </div>

          {/* Save button */}
          <div className="flex items-center justify-between py-4">
            <div className="text-sm text-surface-500">
              {hasChanges && 'You have unsaved changes'}
              {saved && (
                <span className="flex items-center gap-1.5 text-emerald-600">
                  <CheckIcon className="w-4 h-4" />
                  Changes saved
                </span>
              )}
            </div>
            <button
              onClick={handleSave}
              disabled={!hasChanges || saving}
              className={`px-6 py-2.5 rounded-lg font-medium transition-all ${
                hasChanges && !saving
                  ? 'bg-brand-600 text-white hover:bg-brand-700 shadow-sm'
                  : 'bg-surface-200 text-surface-400 cursor-not-allowed'
              }`}
            >
              {saving ? 'Saving...' : 'Save changes'}
            </button>
          </div>
        </div>
      </main>
    </div>
  )
}

